/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import React, { useEffect } from 'react';
import { RichTicket } from '../../../@interfaces/ticket/ticket';
import {
  calculateTicketConsumption,
  TicketPlanCourse,
} from '../../../@interfaces/ticket/ticket-plan-course';

const styles = {
  container: css`
    background: #ffffff;
    border-radius: 4px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  `,
  header: css`
    display: flex;
    align-items: center;
    font-size: 16px;
    color: #333;
    margin-bottom: 16px;
    background: #fef6e7;
    padding: 12px 16px;
    border-radius: 4px;
  `,
  icon: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: #ff6b00;
    color: white;
    border-radius: 50%;
    margin-right: 8px;
    font-weight: bold;
    font-size: 14px;
  `,
  ticketOption: css`
    display: flex;
    align-items: center;
    padding: 16px;
    border-radius: 8px;
    transition: background-color 0.2s;

    &:hover {
      background-color: #f5f5f5;
    }
  `,
  radioButton: css`
    display: none;
  `,
  customRadio: css`
    position: relative;
    display: inline-block;
    vertical-align: middle;
    width: 20px;
    height: 20px;
    border: 2px solid #a2aab7;
    border-radius: 50%;
    margin-right: 12px;
    transition: border-color 0.2s;

    &.checked {
      border-color: #00bcd4;

      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: #00bcd4;
      }
    }
  `,
  ticketLabel: css`
    font-size: 16px;
    color: #333;
    cursor: pointer;
    flex: 1;
    display: flex;
    align-items: center;
  `,
  ticketName: css`
    font-weight: 500;
  `,
  quantitySection: css`
    display: inline-flex;
    align-items: center;
    border-radius: 4px;
  `,
  quantityText: css`
    font-weight: bold;
    color: #333;
    margin: 0 4px;
  `,
  calculationDetail: css`
    font-size: 12px;
    color: #666;
    margin-left: 8px;
  `,
  ticketInfo: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  `,
  disabledTicket: css`
    opacity: 0.5;
    cursor: not-allowed;
  `,
  errorMessage: css`
    font-size: 12px;
    color: #e74c3c;
    font-weight: 500;
    margin-left: 8px;
  `,
};

type TicketSelectorProps = {
  tickets: RichTicket[];
  ticketPlanCourses: TicketPlanCourse[];
  selectedTicketId: number | null;
  onTicketChange?: (ticketId: number | null) => void;
  minuteRequired?: number;
  onValidationChange?: (isValid: boolean) => void;
};

const TicketSelector: React.FC<TicketSelectorProps> = ({
  tickets,
  ticketPlanCourses,
  selectedTicketId,
  onTicketChange,
  minuteRequired,
  onValidationChange,
}) => {
  // 単位の接尾辞を取得（回、ポイント、時間、円など）
  const getUnitSuffix = (ticket: RichTicket): string => {
    return ticket.unitSuffix || '枚';
  };

  // チケットの必要枚数を計算
  const calculateRequiredAmount = (ticketPlanId: number): number => {
    const planCourse = ticketPlanCourses.find(
      (tpc) => tpc.ticketPlanId === ticketPlanId
    );

    if (!planCourse) {
      return 1;
    }

    return calculateTicketConsumption(planCourse, minuteRequired ?? null);
  };

  // 計算詳細を取得
  const getCalculationDetail = (ticketPlanId: number): string => {
    const planCourse = ticketPlanCourses.find(
      (tpc) => tpc.ticketPlanId === ticketPlanId
    );

    if (!planCourse) {
      return '';
    }

    // 所要時間ベースの計算
    if (
      (planCourse.calculationType === 'per_duration' &&
        planCourse.perDurationMinutes &&
        planCourse.perDurationAmount &&
        minuteRequired) ??
      null
    ) {
      return `（${minuteRequired}分 ÷ ${planCourse.perDurationMinutes}分 × ${planCourse.perDurationAmount}枚）`;
    }

    return '（固定）';
  };

  // チケットが使用可能かチェック
  const isTicketUsable = (ticket: RichTicket): boolean => {
    const requiredAmount = calculateRequiredAmount(ticket.ticketPlanId);
    return ticket.remainingCount >= requiredAmount;
  };

  // 不足メッセージを取得
  const getInsufficientMessage = (ticket: RichTicket): string => {
    const requiredAmount = calculateRequiredAmount(ticket.ticketPlanId);
    const shortage = requiredAmount - ticket.remainingCount;
    return `残数不足（あと${shortage}${getUnitSuffix(ticket)}分必要）`;
  };

  // バリデーション状態を監視
  useEffect(() => {
    if (onValidationChange) {
      // チケット未選択の場合は有効とする
      if (selectedTicketId === null) {
        onValidationChange(true);
        return;
      }

      // 選択されたチケットが使用可能かチェック
      const selectedTicket = tickets.find((t) => t.id === selectedTicketId);
      if (selectedTicket) {
        const isValid = isTicketUsable(selectedTicket);
        onValidationChange(isValid);
      } else {
        onValidationChange(false);
      }
    }
  }, [
    selectedTicketId,
    tickets,
    ticketPlanCourses,
    minuteRequired,
    onValidationChange,
  ]);

  return (
    <div>
      <div css={styles.header}>
        <span css={styles.icon}>i</span>
        <span>下記が利用可能です。</span>
      </div>

      <div css={styles.container}>
        {tickets.map((ticket) => {
          const isUsable = isTicketUsable(ticket);
          return (
            <div
              key={ticket.id}
              css={[styles.ticketOption, !isUsable && styles.disabledTicket]}
            >
              <input
                type="radio"
                id={`ticket-${ticket.id}`}
                name="ticket"
                value={ticket.id}
                checked={selectedTicketId === ticket.id}
                onChange={(e) => {
                  if (onTicketChange && isUsable) {
                    onTicketChange(Number(e.target.value));
                  }
                }}
                disabled={!isUsable}
                css={styles.radioButton}
              />
              <label htmlFor={`ticket-${ticket.id}`} css={styles.ticketLabel}>
                <span
                  css={styles.customRadio}
                  className={selectedTicketId === ticket.id ? 'checked' : ''}
                />
                <div css={styles.ticketInfo}>
                  <div>
                    <span css={styles.ticketName}>
                      {ticket.ticketPlanName || 'チケット'}
                    </span>
                    を
                    <span css={styles.quantitySection}>
                      <span css={styles.quantityText}>
                        {calculateRequiredAmount(ticket.ticketPlanId)}
                      </span>
                      <span>{getUnitSuffix(ticket)}分使う</span>
                    </span>
                  </div>
                  <span css={styles.calculationDetail}>
                    {getCalculationDetail(ticket.ticketPlanId)}
                  </span>
                  {!isUsable && (
                    <span css={styles.errorMessage}>
                      {getInsufficientMessage(ticket)}
                    </span>
                  )}
                </div>
              </label>
            </div>
          );
        })}

        {/* チケットを利用しない選択肢 */}
        <div css={styles.ticketOption}>
          <input
            type="radio"
            id="ticket-none"
            name="ticket"
            value="none"
            checked={selectedTicketId === null}
            onChange={() => {
              if (onTicketChange) {
                onTicketChange(null);
              }
            }}
            css={styles.radioButton}
          />
          <label htmlFor="ticket-none" css={styles.ticketLabel}>
            <span
              css={styles.customRadio}
              className={selectedTicketId === null ? 'checked' : ''}
            />
            <span css={styles.ticketName}>チケットを利用しない</span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default TicketSelector;
